import type { ContentLang } from '@/features/editor/editorSlice'
import type { TranslationRunView } from '@/features/ai/lib/translationRun'
import type { useCancelTranslationRun } from '@/features/ai/useCancelTranslationRun'
import type { TranslationDialog } from './TranslationDialogs'
import { TranslationPanel } from './TranslationPanel'
import { RunFrame, TranslationRunning, TranslationSummary } from './TranslationRunBlock'
import type { TranslationLauncher } from './useTranslationLauncher'

interface Props {
  view: TranslationRunView
  targetLang: ContentLang
  launcher: TranslationLauncher
  canceller: ReturnType<typeof useCancelTranslationRun>
  onAsk: (d: Exclude<TranslationDialog, null>) => void
  /** Показывать ли итог законченного прогона. Решает `useMenuTranslation`:
   *  содержимое меню размонтируется на каждом закрытии, и своё состояние
   *  здесь не пережило бы его. */
  showSummary: boolean
  onDismiss: (jobId: number) => void
}

/**
 * Подвал меню про перевод (спека §05): идёт прогон — полоса и отмена вместо
 * панели запуска; закончился — итог над панелью, пока автор его не скроет.
 * Черту над подвалом рисует меню: над ним стоит подсказка про базовый язык,
 * и отделять её от объёма перевода макет не велит.
 */
export const TranslationSection = ({
  view,
  targetLang,
  launcher,
  canceller,
  onAsk,
  showSummary,
  onDismiss,
}: Props) => {
  if (view.kind === 'unknown') {
    return (
      <TranslationRunning
        view={view}
        targetLang={targetLang}
        onAskCancel={() => onAsk('cancel')}
        isCancelling={canceller.isCancelling}
        cancelFailure={canceller.failure}
      />
    )
  }
  const summary = view.kind === 'finished' && showSummary ? view : null
  return (
    <>
      {view.kind === 'running' || summary ? (
        <RunFrame success={summary?.status === 'success'}>
          {view.kind === 'running' ? (
            <TranslationRunning
              view={view}
              targetLang={targetLang}
              onAskCancel={() => onAsk('cancel')}
              isCancelling={canceller.isCancelling}
              cancelFailure={canceller.failure}
            />
          ) : summary ? (
            <TranslationSummary view={summary} onDismiss={() => onDismiss(summary.jobId)} />
          ) : null}
        </RunFrame>
      ) : null}
      {view.kind === 'running' ? null : <TranslationPanel launcher={launcher} onAsk={onAsk} />}
    </>
  )
}
